import React, { useState } from 'react';
import { Box, Container, Typography, Alert, Paper, List, ListItem, ListItemText, Chip, Switch, FormControlLabel, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import CameraView from '../../components/FieldAgent/CameraView';
import ManualPlateInput from '../../components/FieldAgent/ManualPlateInput';
import VehicleQuickActions from './VehicleQuickActions';
import CreateVehicleModal from '../../components/FieldAgent/CreateVehicleModal';
import { Vehicle } from '../../types';
import { vehicleService, activityService } from '../../services/api';
import { getStatusLabel, getStatusColor } from '../../utils/vehicleUtils';

const Scanner: React.FC = () => {
  const [scannedPlate, setScannedPlate] = useState<string>('');
  const [foundVehicle, setFoundVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [recentVehicles, setRecentVehicles] = useState<Vehicle[]>([]);
  const [useCameraScanner, setUseCameraScanner] = useState(true);
  const [showCreateVehicle, setShowCreateVehicle] = useState(false);

  const handlePlateDetected = async (plateNumber: string) => {
    setScannedPlate(plateNumber);
    setLoading(true);
    setError('');

    try {
      // Search for vehicle by plate number
      const response = await vehicleService.getAll({ 
        search: plateNumber 
      });
      
      const vehicle = response.data.data.find(
        v => v.registration_number.toLowerCase() === plateNumber.toLowerCase()
      );

      if (vehicle) {
        setFoundVehicle(vehicle);
        
        // Log scan activity
        try {
          await activityService.logScan(vehicle.id, plateNumber);
        } catch (err) {
          console.error('Error logging scan:', err);
          // Don't show error to user, just log it
        }
        
        // Add to recent vehicles
        setRecentVehicles(prev => {
          const filtered = prev.filter(v => v.id !== vehicle.id);
          return [vehicle, ...filtered].slice(0, 5);
        });
        
        // Haptic feedback (vibration)
        if (navigator.vibrate) {
          navigator.vibrate(200);
        }
      } else {
        setError(`Aucun véhicule trouvé avec la plaque ${plateNumber}`);
        setScannedPlate(plateNumber); // Keep the scanned plate for creating new vehicle
      }
    } catch (err) {
      console.error('Error searching vehicle:', err);
      setError('Erreur lors de la recherche du véhicule');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFoundVehicle(null);
    setScannedPlate('');
    setError('');
  };

  const handleCreateVehicleSuccess = (vehicle: Vehicle) => {
    setFoundVehicle(vehicle);
    setError('');
    setShowCreateVehicle(false);
    
    // Add to recent vehicles
    setRecentVehicles(prev => {
      const filtered = prev.filter(v => v.id !== vehicle.id);
      return [vehicle, ...filtered].slice(0, 5);
    });
    
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }
  };

  const handleRecentVehicleClick = (vehicle: Vehicle) => {
    setFoundVehicle(vehicle);
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        minHeight: '100%',
        pt: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 3 },
        pb: { xs: 20, sm: 12 }, // Extra padding to avoid footer overlap on mobile
      }}
    >
      <Container maxWidth="md" disableGutters sx={{ height: '100%' }}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Scanner Véhicule
          </Typography>
          <FormControlLabel
            control={
              <Switch 
                checked={useCameraScanner}
                onChange={(e) => setUseCameraScanner(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Typography variant="caption">
                {useCameraScanner ? '📷 Caméra' : '⌨️ Manuel'}
              </Typography>
            }
          />
        </Box>

        {/* Camera Scanner */}
        {useCameraScanner ? (
          <Box sx={{ mb: 3 }}>
            <CameraView 
              onPlateDetected={handlePlateDetected}
              isScanning={true}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              💡 Astuce: Bonne luminosité et plaque propre pour meilleure reconnaissance
            </Typography>
          </Box>
        ) : (
          /* Manual Entry */
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
              Saisir manuellement le numéro de plaque
            </Typography>
            <ManualPlateInput onSubmit={handlePlateDetected} loading={loading} />
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }} 
            onClose={() => setError('')}
            action={
              error.includes('Aucun véhicule trouvé') && scannedPlate ? (
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateVehicle(true)}
                  sx={{ ml: 1 }}
                >
                  Créer véhicule
                </Button>
              ) : null
            }
          >
            {error}
          </Alert>
        )}

        {/* Car Background Image - Show when camera mode and no recent vehicles */}
        {!error && recentVehicles.length === 0 && useCameraScanner && (
          <Box 
            sx={{ 
              position: 'relative',
              width: '100%',
              minHeight: { xs: '300px', sm: '400px' },
              borderRadius: 2,
              overflow: 'hidden',
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/fieldagent/bg.jpg)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                zIndex: 1,
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center', px: 3 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 2,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}
              >
                Prêt à scanner
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'white',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }}
              >
                Scannez une plaque d'immatriculation pour commencer
              </Typography>
            </Box>
          </Box>
        )}

        {/* Recent Scans */}
        {recentVehicles.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
              Véhicules Récents
            </Typography>
            <List disablePadding>
              {recentVehicles.map((vehicle) => (
                <Paper 
                  key={vehicle.id}
                  elevation={1}
                  sx={{ 
                    mb: 1, 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      elevation: 2,
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => handleRecentVehicleClick(vehicle)}
                >
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {vehicle.registration_number}
                          </Typography>
                          <Chip 
                            label={getStatusLabel(vehicle.status)}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(vehicle.status),
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {vehicle.marque} {vehicle.modele}
                        </Typography>
                      }
                    />
                  </ListItem>
                </Paper>
              ))}
            </List>
          </Box>
        )}
      </Box>

      {/* Quick Actions Modal */}
      {foundVehicle && (
        <VehicleQuickActions
          vehicle={foundVehicle}
          open={!!foundVehicle}
          onClose={handleClose}
        />
      )}

      {/* Create Vehicle Modal */}
      <CreateVehicleModal
        open={showCreateVehicle}
        onClose={() => setShowCreateVehicle(false)}
        onSuccess={handleCreateVehicleSuccess}
        initialPlateNumber={scannedPlate}
      />
      </Container>
    </Box>
  );
};

export default Scanner;

